import { getAdviserBusinessStatus } from "../../../Utils/adviserBusinessStatus";
// src/components/Adviser/Pages/LicenseCertificates/utils/licenseCertificateTables.js

import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../../../../../backend/firebaseConfig";
import { COMPETENT_ADVISER_TEST_FIELDS } from "../utils/LicenseCertificateFields";
import { formatCpdHours } from "./cpdRecords";
import { calculateCpdProgress } from "./cpdProgress";

const ADVISERS_PATH = ["Authorized Members", "Advisers", "Data"];

const RULES_REF_PATH = [
  "Authorized Members",
  "Advisers",
  "License Certificates",
  "Follow Up Rules",
];

const DAY_MS = 24 * 60 * 60 * 1000;

export const formatDateTime = (value) => {
  if (!value) return "-";

  const date = value?.toDate ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getNestedValueByPath = (source = {}, path = "") => {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((acc, key) => acc?.[key], source);
};

const formatReminderText = (period = "") => {
  const value = String(period || "")
    .trim()
    .toLowerCase();

  const match = value.match(/^(\d+)(d|w|m)$/);

  if (!match) return value;

  const amount = Number(match[1]);
  const unit = match[2];

  const labels = {
    d: amount === 1 ? "day" : "days",
    w: amount === 1 ? "week" : "weeks",
    m: amount === 1 ? "month" : "months",
  };

  return `${amount} ${labels[unit]}`;
};

const getFieldUpdatedLabel = (licenseData = {}, fieldPath = "", label = "") => {
  const fieldUpdate = getNestedValueByPath(
    licenseData?.fieldUpdateSummary || {},
    fieldPath,
  );

  if (fieldUpdate?.updatedAt) {
    return [
      `${label || fieldPath}`,
      `Last updated by ${
        fieldUpdate.updatedByName || fieldUpdate.updatedBy || "Unknown"
      }`,
      `Updated on ${formatDateTime(fieldUpdate.updatedAt)}`,
    ].join("\n");
  }

  if (licenseData?.updatedAt) {
    return [
      `${label || fieldPath}`,
      `Last updated by ${
        licenseData.updatedByName || licenseData.updatedBy || "Unknown"
      }`,
      `Updated on ${formatDateTime(licenseData.updatedAt)}`,
    ].join("\n");
  }

  return `${label || fieldPath}\nNo update details found.`;
};

const getFollowUpSummary = (licenseData = {}, followUpKey = "") => {
  return (
    licenseData?.followUpSummary?.[followUpKey] ||
    getNestedValueByPath(licenseData?.followUpSummary || {}, followUpKey) ||
    {}
  );
};

const getFollowUpMeta = (licenseData = {}, followUpKey = "", label = "") => {
  const summary = getFollowUpSummary(licenseData, followUpKey);

  const lastSentAt = summary?.lastSentAt || "";
  const lastReminderDate = summary?.lastReminderDate || "";
  const lastPeriod = summary?.lastPeriod || "";
  const lastFromName = summary?.lastFromName || "";
  const lastFromEmail = summary?.lastFromEmail || "";
  const lastSubject = summary?.lastSubject || "";

  const sentDateLabel = lastSentAt
    ? formatDateTime(lastSentAt)
    : formatDateOnly(lastReminderDate);

  const senderLabel =
    lastFromName || lastFromEmail || summary?.lastSentBy || "server";

  const lastFollowUpLabel =
    sentDateLabel && sentDateLabel !== "-"
      ? [
          `${label || followUpKey} follow-up sent`,
          `Sent by ${senderLabel}`,
          `Sent on ${sentDateLabel}`,
          lastPeriod
            ? `Reminder period: ${formatReminderText(lastPeriod)}`
            : "",
          lastSubject ? `Subject: ${lastSubject}` : "",
        ]
          .filter(Boolean)
          .join("\n")
      : `No ${label || followUpKey} follow-up has been sent yet.`;

  return {
    lastFollowUp: sentDateLabel || "-",
    lastFollowUpSentAt: lastSentAt || lastReminderDate || "",
    lastFollowUpLabel,
    lastFollowUpPeriod: lastPeriod,
    lastFollowUpSubject: lastSubject,
    lastFollowUpFromName: lastFromName,
    lastFollowUpFromEmail: lastFromEmail,
  };
};

export const FOLLOW_UP_RULE_DEFAULTS = {
  dplCertificate: {
    label: "DPL",
    sendTo: "Adviser",
    cc: "Andre",
    bcc: "",
    reminderOffsetsDays: [30, 7],
    enabled: true,
  },
  spsCertificate: {
    label: "SPS",
    sendTo: "Adviser",
    cc: "Andre",
    bcc: "",
    reminderOffsetsDays: [30, 7],
    enabled: true,
  },
  fitAndProper: {
    label: "Fit & Proper",
    sendTo: "Adviser",
    cc: "Neshitha",
    bcc: "",
    reminderOffsetsDays: [30, 14, 5],
    enabled: true,
  },
  tc: {
    label: "T&C",
    sendTo: "Adviser",
    cc: "Neshitha & Grace",
    bcc: "",
    reminderOffsetsDays: [30],
    enabled: true,
  },
};

export const formatReminderPeriod = (days = []) => {
  const cleaned = [...new Set(days)]
    .map(Number)
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => b - a);

  if (!cleaned.length) return "-";

  return cleaned
    .map((day) => {
      if (day === 30) return "1 month";
      if (day === 14) return "2 weeks";
      if (day === 7) return "7 days";
      if (day === 5) return "5 days";
      if (day % 7 === 0) return `${day / 7} weeks`;
      return `${day} days`;
    })
    .join(", ");
};

export const parseReminderPeriodInput = (value) => {
  return String(value || "")
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item) && item > 0);
};

export const getRulesRef = () => doc(db, ...RULES_REF_PATH);

export const loadLicenseCertificateFollowUpRules = async () => {
  const snap = await getDoc(getRulesRef());

  if (!snap.exists()) {
    return FOLLOW_UP_RULE_DEFAULTS;
  }

  return {
    ...FOLLOW_UP_RULE_DEFAULTS,
    ...(snap.data()?.rules || {}),
  };
};

export const saveLicenseCertificateFollowUpRules = async (rules) => {
  await setDoc(
    getRulesRef(),
    {
      rules,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.email || "",
    },
    { merge: true },
  );

  return rules;
};

const isBlankDate = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();
  return !text || text === "n/a" || text === "na" || text === "-";
};

export const parseDateOnly = (value) => {
  if (isBlankDate(value)) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === "object" && value?.seconds != null) {
    const date = new Date(value.seconds * 1000);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  const text = String(value || "").trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(text)) {
    const [day, month, yearValue] = text.split("/").map(Number);
    const year = yearValue < 100 ? 2000 + yearValue : yearValue;
    return new Date(year, month - 1, day);
  }

  return null;
};

export const formatDateOnly = (value) => {
  const date = parseDateOnly(value);

  if (!date) {
    return isBlankDate(value) ? "-" : String(value || "-");
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const toIsoDate = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const minusDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() - Number(days));
  return copy;
};

const addMonthsClamped = (date, months) => {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0,
  ).getDate();

  target.setDate(Math.min(date.getDate(), lastDay));
  return target;
};

const getReminderDates = (deadlineValue, rule) => {
  const deadline = parseDateOnly(deadlineValue);

  if (!deadline) return [];

  return (rule?.reminderOffsetsDays || [])
    .map((days) => minusDays(deadline, days))
    .sort((a, b) => a.getTime() - b.getTime())
    .map(toIsoDate);
};

const getDateStatus = (deadlineValue, rule) => {
  const deadline = parseDateOnly(deadlineValue);

  if (!deadline) {
    return {
      status: "No date",
      statusClass: "noDate",
      daysUntil: null,
      reminderDates: [],
    };
  }

  const today = new Date();
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  const daysUntil = Math.ceil(
    (deadline.getTime() - todayOnly.getTime()) / DAY_MS,
  );

  const maxReminderDays = Math.max(...(rule?.reminderOffsetsDays || [30]));

  if (daysUntil < 0) {
    return {
      status: "Expired",
      statusClass: "expired",
      daysUntil,
      reminderDates: getReminderDates(deadlineValue, rule),
    };
  }

  if (daysUntil <= maxReminderDays) {
    return {
      status: "Due soon",
      statusClass: "dueSoon",
      daysUntil,
      reminderDates: getReminderDates(deadlineValue, rule),
    };
  }

  return {
    status: "OK",
    statusClass: "ok",
    daysUntil,
    reminderDates: getReminderDates(deadlineValue, rule),
  };
};

const getCompetentAdviserTestStatus = (testValue, rule) => {
  const testDate = parseDateOnly(testValue);

  if (!testDate) return getDateStatus(testValue, rule);

  const warningDate = addMonthsClamped(testDate, 11);
  const expiryDate = addMonthsClamped(testDate, 12);
  const expiryValue = toIsoDate(expiryDate);
  const status = getDateStatus(expiryValue, rule);
  const today = new Date();
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  if (todayOnly >= expiryDate) {
    return {
      ...status,
      status: "Expired",
      statusClass: "expired",
      deadline: expiryValue,
    };
  }

  if (todayOnly >= warningDate) {
    return {
      ...status,
      status: "Due soon",
      statusClass: "dueSoon",
      deadline: expiryValue,
    };
  }

  return {
    ...status,
    status: "OK",
    statusClass: "ok",
    deadline: expiryValue,
  };
};

const getAdviserName = (base = {}, personal = {}) => {
  const fullName = [personal?.forename, personal?.surname]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    fullName ||
    base.name ||
    base.displayName ||
    base.tradingName ||
    base.tradingStyle ||
    base.email ||
    "Adviser"
  );
};

const getLastFollowUp = (licenseData, key) => {
  const value = licenseData?.followUpSummary?.[key]?.lastSentAt;
  return formatDateOnly(value);
};

const makeBaseRow = ({
  adviserId,
  base,
  personal,
  licenseData,
}) => {
  const adviserBusinessStatus = getAdviserBusinessStatus(base);

  return {
    adviserId,
    adviserName: getAdviserName(base, personal),
    adviserEmail: base?.email || personal?.emailAddress || adviserId,
    adviserBusinessStatus,
    dateAppointed: base?.dateAppointed || "",
    hasFpPermission: adviserBusinessStatus === "FP",
    licenseData,
  };
};

export const loadLicenseCertificateTableRows = async () => {
  const rules = await loadLicenseCertificateFollowUpRules();

  const advisersSnap = await getDocs(collection(db, ...ADVISERS_PATH));

  const adviserRows = await Promise.all(
    advisersSnap.docs
      .filter((adviserDoc) => {
        const base = adviserDoc.data() || {};
        return base.isAppointed !== false && base.isIndroducerAR !== true;
      })
      .map(async (adviserDoc) => {
      const adviserId = adviserDoc.id;
      const base = adviserDoc.data() || {};

      const [personalSnap, applicationSnap, licenseSnap] = await Promise.all([
        getDoc(
          doc(db, ...ADVISERS_PATH, adviserId, "Personal Details", "Data"),
        ),
        getDoc(
          doc(db, ...ADVISERS_PATH, adviserId, "Application Details", "Data"),
        ),
        getDoc(
          doc(db, ...ADVISERS_PATH, adviserId, "License Certificates", "Data"),
        ),
      ]);

      return makeBaseRow({
        adviserId,
        base,
        personal: personalSnap.exists() ? personalSnap.data() : {},
        application: applicationSnap.exists() ? applicationSnap.data() : {},
        licenseData: licenseSnap.exists() ? licenseSnap.data() : {},
      });
      }),
  );

  const hiddenAdvisers = adviserRows.filter(
    (adviser) => !adviser.adviserBusinessStatus,
  );

  if (hiddenAdvisers.length > 0) {
    const adviserDataById = new Map(
      advisersSnap.docs.map((adviserDoc) => [adviserDoc.id, adviserDoc.data()]),
    );
    // console.groupCollapsed(
    //   `[Licence Certificates] ${hiddenAdvisers.length} appointed adviser(s) hidden by blank or unrecognised status`,
    // );
    // console.table(
    //   hiddenAdvisers.map((adviser) => {
    //     const base = adviserDataById.get(adviser.adviserId) || {};
    //     return {
    //       name: adviser.adviserName,
    //       adviserId: adviser.adviserId,
    //       status: JSON.stringify(base.status ?? null),
    //       adviserStatus: JSON.stringify(base.adviserStatus ?? null),
    //       businessStatus: JSON.stringify(base.businessStatus ?? null),
    //     };
    //   }),
    // );
    // console.groupEnd();
  }

  const dplRows = [];
  const spsRows = [];
  const fitAndProperRows = [];
  const competentAdviserRows = [];
  const tcRows = [];
  const cpdRows = [];
  const allCpdRows = [];
  const iddCpdRows = [];
  const allRows = [];

  adviserRows.forEach((adviser) => {
    const data = adviser.licenseData || {};

    const dplStatus = getDateStatus(
      data?.dplCertificate?.dplExpiryDate,
      rules.dplCertificate,
    );

    const dplRow = {
      id: `${adviser.adviserId}-dpl`,
      adviserId: adviser.adviserId,
      category: "DPL",
      adviserBusinessStatus: adviser.adviserBusinessStatus,
      adviserName: adviser.adviserName,
      adviserEmail: adviser.adviserEmail,
      dplName: data?.dplCertificate?.dplName || "-",
      dplRegistrationNumber: data?.dplCertificate?.dplRegistrationNumber || "-",
      deadline: data?.dplCertificate?.dplExpiryDate || "",
      deadlineLabel: "DPL Expiry",
      lastFollowUp: getLastFollowUp(data, "dplCertificate"),
      updatedLabel: getFieldUpdatedLabel(
        data,
        "dplCertificate.dplExpiryDate",
        "DPL Expiry Date",
      ),
      statusSourceLabel: "DPL Expiry Date",
      ...getFollowUpMeta(data, "dplCertificate", "DPL"),
      ...dplStatus,
    };

    dplRows.push(dplRow);
    allRows.push(dplRow);

    const spsStatus = getDateStatus(
      data?.spsCertificate?.spsExpiryDate,
      rules.spsCertificate,
    );

    const spsRow = {
      id: `${adviser.adviserId}-sps`,
      adviserId: adviser.adviserId,
      category: "SPS",
      adviserName: adviser.adviserName,
      adviserEmail: adviser.adviserEmail,
      adviserBusinessStatus: adviser.adviserBusinessStatus,
      spsAccreditedBody: data?.spsCertificate?.spsAccreditedBody || "-",
      deadline: data?.spsCertificate?.spsExpiryDate || "",
      deadlineLabel: "SPS Expiry",
      lastFollowUp: getLastFollowUp(data, "spsCertificate"),
      updatedLabel: getFieldUpdatedLabel(
        data,
        "spsCertificate.spsExpiryDate",
        "SPS Expiry Date",
      ),
      statusSourceLabel: "SPS Expiry Date",
      ...getFollowUpMeta(data, "spsCertificate", "SPS"),
      ...spsStatus,
    };

    spsRows.push(spsRow);
    allRows.push(spsRow);

    const fitStatus = getDateStatus(
      data?.fitAndProper?.fitAndProperExpiryDate,
      rules.fitAndProper,
    );

    const fitRow = {
      id: `${adviser.adviserId}-fit`,
      adviserId: adviser.adviserId,
      category: "Fit & Proper",
      adviserBusinessStatus: adviser.adviserBusinessStatus,
      adviserName: adviser.adviserName,
      adviserEmail: adviser.adviserEmail,
      deadline: data?.fitAndProper?.fitAndProperExpiryDate || "",
      creditScore: data?.fitAndProper?.creditScore || "",
      creditScoreDate: data?.fitAndProper?.creditScoreDate || "",
      creditReportDate: data?.fitAndProper?.creditReportDate || "",
      ccAdvisers: Array.isArray(data?.fitAndProper?.ccAdvisers)
        ? data.fitAndProper.ccAdvisers
        : [],
      notes: data?.fitAndProper?.notes || "",
      deadlineLabel: "Fit & Proper Expiry",
      lastFollowUp: getLastFollowUp(data, "fitAndProper"),
      updatedLabel: getFieldUpdatedLabel(
        data,
        "fitAndProper.fitAndProperExpiryDate",
        "Fit & Proper Expiry Date",
      ),
      statusSourceLabel: "Fit & Proper Expiry Date",
      ...getFollowUpMeta(data, "fitAndProper", "Fit & Proper"),
      ...fitStatus,
    };

    fitAndProperRows.push(fitRow);
    allRows.push(fitRow);

    COMPETENT_ADVISER_TEST_FIELDS.forEach((field) => {
      const testValue = data?.competentAdviser?.[field.key] || "";
      const testStatus = getCompetentAdviserTestStatus(
        testValue,
        rules.fitAndProper,
      );

      const fieldPath = `competentAdviser.${field.key}`;

      const row = {
        id: `${adviser.adviserId}-competent-${field.key}`,
        adviserId: adviser.adviserId,
        category: "Competent Adviser",
        adviserName: adviser.adviserName,
        adviserEmail: adviser.adviserEmail,
        adviserBusinessStatus: adviser.adviserBusinessStatus,
        testName: field.label,
        testKey: field.key,
        testDate: testValue,
        deadline: testStatus.deadline || "",
        deadlineLabel: field.label,
        updatedLabel: getFieldUpdatedLabel(data, fieldPath, field.label),
        statusSourceLabel: field.label,
        ...getFollowUpMeta(data, fieldPath, field.label),
        ...testStatus,
      };
      competentAdviserRows.push(row);
      allRows.push(row);
    });

    const tcStatus = getDateStatus(data?.tc?.nextOcaMeeting, rules.tc);

    const tcRow = {
      id: `${adviser.adviserId}-tc`,
      adviserId: adviser.adviserId,
      category: "T&C",
      adviserName: adviser.adviserName,
      adviserEmail: adviser.adviserEmail,
      adviserBusinessStatus: adviser.adviserBusinessStatus,
      appointmentDate: data?.tc?.appointmentDate || "",
      preCASSignOff: data?.tc?.preCASSignOff || "",
      lastOcaMeeting: data?.tc?.lastOcaMeeting || "",
      nextOcaMeeting: data?.tc?.nextOcaMeeting || "",
      deadline: data?.tc?.nextOcaMeeting || "",
      deadlineLabel: "Next OCA Meeting",
      lastFollowUp: getLastFollowUp(data, "tc"),
      updatedLabel: getFieldUpdatedLabel(
        data,
        "tc.nextOcaMeeting",
        "Next OCA Meeting",
      ),
      statusSourceLabel: "Next OCA Meeting",
      ...getFollowUpMeta(data, "tc", "T&C"),
      ...tcStatus,
    };

    tcRows.push(tcRow);
    allRows.push(tcRow);

    const allCpd = data?.cpd?.all;
    const iddCpd = data?.cpd?.idd;
    const isCpdStatus = ["FP", "MA", "GI/Prot"].includes(
      adviser.adviserBusinessStatus,
    );
    const cpdProgress = isCpdStatus
      ? calculateCpdProgress({
          allCpd,
          iddCpd,
          isFinancialPlanner: adviser.hasFpPermission,
          adviserType: adviser.adviserBusinessStatus,
          appointmentDate: adviser.dateAppointed,
          spsExpiryDate: data?.spsCertificate?.spsExpiryDate,
        })
      : null;

    if (isCpdStatus && allCpd) {
      allCpdRows.push({
        id: `${adviser.adviserId}-all-cpd`,
        adviserId: adviser.adviserId,
        adviserName: adviser.adviserName,
        adviserEmail: adviser.adviserEmail,
        adviserBusinessStatus: adviser.adviserBusinessStatus,
        reportDate: allCpd.reportDate || "",
        cpdEntries: allCpd.cpdEntries ?? 0,
        totalCpdHours: formatCpdHours(allCpd.cpdMinutes),
        structuredEntries: allCpd.structuredEntries ?? 0,
        structuredHours: formatCpdHours(allCpd.structuredMinutes),
        nonStructuredEntries: allCpd.nonStructuredEntries ?? 0,
        nonStructuredHours: formatCpdHours(allCpd.nonStructuredMinutes),
        ...cpdProgress,
      });
    }

    if (isCpdStatus && iddCpd) {
      iddCpdRows.push({
        id: `${adviser.adviserId}-idd-cpd`,
        adviserId: adviser.adviserId,
        adviserName: adviser.adviserName,
        adviserEmail: adviser.adviserEmail,
        adviserBusinessStatus: adviser.adviserBusinessStatus,
        reportDate: iddCpd.reportDate || "",
        cpdEntries: iddCpd.cpdEntries ?? 0,
        iddHours: formatCpdHours(iddCpd.cpdMinutes),
        structuredEntries: iddCpd.structuredEntries ?? 0,
        structuredHours: formatCpdHours(iddCpd.structuredMinutes),
        nonStructuredEntries: iddCpd.nonStructuredEntries ?? 0,
        nonStructuredHours: formatCpdHours(iddCpd.nonStructuredMinutes),
        ...cpdProgress,
      });
    }

    if (isCpdStatus && (allCpd || iddCpd)) {
      cpdRows.push({
        id: `${adviser.adviserId}-cpd`,
        adviserId: adviser.adviserId,
        adviserName: adviser.adviserName,
        adviserEmail: adviser.adviserEmail,
        adviserBusinessStatus: adviser.adviserBusinessStatus,
        allReportDate: allCpd?.reportDate || "",
        totalCpdHours: allCpd ? formatCpdHours(allCpd.cpdMinutes) : "-",
        structuredHours: allCpd
          ? formatCpdHours(allCpd.structuredMinutes)
          : "-",
        nonStructuredHours: allCpd
          ? formatCpdHours(allCpd.nonStructuredMinutes)
          : "-",
        iddReportDate: iddCpd?.reportDate || "",
        iddHours: iddCpd ? formatCpdHours(iddCpd.cpdMinutes) : "-",
      });
    }
  });

  const sortRows = (rows) =>
    [...rows].sort((a, b) => {
      if (a.daysUntil === null && b.daysUntil === null) return 0;
      if (a.daysUntil === null) return 1;
      if (b.daysUntil === null) return -1;
      return a.daysUntil - b.daysUntil;
    });

  return {
    rules,
    allRows: sortRows(allRows),
    dplRows: sortRows(dplRows),
    spsRows: sortRows(spsRows),
    fitAndProperRows: sortRows(fitAndProperRows),
    competentAdviserRows: sortRows(competentAdviserRows),
    tcRows: sortRows(tcRows),
    cpdRows: [...cpdRows].sort((a, b) =>
      a.adviserName.localeCompare(b.adviserName),
    ),
    allCpdRows: [...allCpdRows].sort((a, b) =>
      a.adviserName.localeCompare(b.adviserName),
    ),
    iddCpdRows: [...iddCpdRows].sort((a, b) =>
      a.adviserName.localeCompare(b.adviserName),
    ),
  };
};

export const subscribeLicenseCertificateTableRows = ({
  onData,
  onError,
} = {}) => {
  let disposed = false;
  let reloadTimer = null;

  const reloadRows = () => {
    clearTimeout(reloadTimer);

    reloadTimer = setTimeout(async () => {
      try {
        const rows = await loadLicenseCertificateTableRows();

        if (!disposed) {
          onData?.(rows);
        }
      } catch (error) {
        if (!disposed) {
          onError?.(error);
        }
      }
    }, 150);
  };

  const handleListenerError = (source) => (error) => {
    console.error(
      `Failed to listen to licence certificate ${source} updates:`,
      error,
    );
    onError?.(error);
  };

  const unsubscribers = [
    onSnapshot(
      collection(db, ...ADVISERS_PATH),
      reloadRows,
      handleListenerError("adviser"),
    ),
    onSnapshot(
      collectionGroup(db, "Personal Details"),
      reloadRows,
      handleListenerError("personal details"),
    ),
    onSnapshot(
      collectionGroup(db, "Application Details"),
      reloadRows,
      handleListenerError("application details"),
    ),
    onSnapshot(
      collectionGroup(db, "License Certificates"),
      reloadRows,
      handleListenerError("record"),
    ),
    onSnapshot(
      doc(db, ...RULES_REF_PATH),
      reloadRows,
      handleListenerError("follow-up rule"),
    ),
  ];

  return () => {
    disposed = true;
    clearTimeout(reloadTimer);
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
};

